import { useCallback, useState } from 'react';
import { useRouter } from 'expo-router';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import {
  checkIfHasRecentlyDismissedFlexEntryPoint,
  storeDismissedFlexEntryPointDate,
} from '../../utils';
import { useFlexCustomer } from '../useFlexEnrollment';

/**
 * The flex entry point is a component that allows the customer to enroll in the flex program.
 * - Should not be shown if the customer already enrolled to flex.
 * - Should not be shown if the customer has just checked their eligibility.
 * - Should not be shown if the customer has dismissed the flex entry point in the last 30 days.
 */
export const useFlexEntryPoint = (props: UseFlexEntryPointProps) => {
  const { isLoggedIn } = props;

  const { track } = useTelemetry();
  const router = useRouter();
  const isFeatureEnabled = useFeatureFlag('CELS-5522-flex-enabled');

  const { hasFlexPaymentMethod, isLoadingFlexCustomer } = useFlexCustomer();

  const [hasCheckedEligibility, setHasCheckedEligibility] = useState(false);

  const [hasRecentlyDismissedFlexCheck, setHasRecentlyDismissedFlexEntryPoint] = useState(
    checkIfHasRecentlyDismissedFlexEntryPoint(),
  );

  const checkFlexEligibility = useCallback(() => {
    setHasCheckedEligibility(true);
    track('home.hsafsa_campaign_banner.pressed');

    if (!isLoggedIn) {
      router.navigate({
        pathname: '/(auth)/join-or-sign-in',
        params: { redirect: '/hsa-fsa' },
      });
      return;
    }

    router.navigate('/hsa-fsa');
  }, [router, isLoggedIn, track]);

  const dismissFlexEntryPoint = useCallback(() => {
    track('home.hsafsa_campaign_banner.dismissed');
    setHasRecentlyDismissedFlexEntryPoint(true);
    storeDismissedFlexEntryPointDate();
  }, [track]);

  const shouldRenderFlexEntryPoint =
    isFeatureEnabled &&
    !hasFlexPaymentMethod &&
    !isLoadingFlexCustomer &&
    !hasCheckedEligibility &&
    !hasRecentlyDismissedFlexCheck;

  return { shouldRenderFlexEntryPoint, checkFlexEligibility, dismissFlexEntryPoint };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseFlexEntryPointProps = {
  isLoggedIn: boolean;
};
